import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Compass, AlertTriangle, Loader2 } from "lucide-react";
import { discoveryApi, apiErrorMessage } from "@/api/client";
import { ProspectCard } from "@/components/ProspectCard";
import { AddToLeadsDialog } from "@/components/forms/AddToLeadsDialog";
import { EmptyState } from "@/components/EmptyState";
import { inputCls } from "@/components/FormKit";

const SORTS = [
  { v: "priority", label: "FHRS priority" },
  { v: "distance", label: "Distance" },
  { v: "business_name", label: "Name" },
  { v: "rating_date_desc", label: "Newest rating" },
];
// Default centre: central London — refine with a postcode.
const DEFAULT = { lat: 51.5074, lng: -0.1278 };

function Segmented() {
  const navigate = useNavigate();
  return (
    <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" data-testid="leads-segmented">
      <button data-testid="seg-my-leads" onClick={() => navigate("/leads")} className="rounded-lg py-2 text-sm font-semibold text-slate-500">My Leads</button>
      <button data-testid="seg-discover" className="rounded-lg bg-white py-2 text-sm font-semibold text-teal-700 shadow-sm">Prospect Discovery</button>
    </div>
  );
}

export default function Discover() {
  const [postcode, setPostcode] = useState("");
  const [radius, setRadius] = useState(2);
  const [sort, setSort] = useState("priority");
  const [items, setItems] = useState([]);
  const [state, setState] = useState("idle"); // idle|loading|ok|error|unavailable
  const [errorMsg, setErrorMsg] = useState("");
  const [centre, setCentre] = useState(DEFAULT);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const run = useCallback(async (opts = {}) => {
    setState("loading");
    try {
      let res;
      if (opts.postcode) {
        res = await discoveryApi.byPostcode({ postcode: opts.postcode, radius_km: radius, sort, page: opts.page || 1, size: 25 });
        if (res.data.centre) setCentre({ lat: res.data.centre.lat, lng: res.data.centre.lng });
      } else {
        const c = opts.centre || centre;
        res = await discoveryApi.nearby({ lat: c.lat, lng: c.lng, radius_km: radius, sort, page: opts.page || 1, size: 25 });
      }
      setItems(res.data.items);
      setPage(opts.page || 1);
      setState("ok");
    } catch (err) {
      if (err?.response?.data?.error?.code === "FSA_UNAVAILABLE") { setState("unavailable"); }
      else if (err?.response?.data?.error?.code === "POSTCODE_UNAVAILABLE") { setState("error"); setErrorMsg("Could not resolve that postcode."); }
      else { setState("error"); setErrorMsg(apiErrorMessage(err)); }
    }
  }, [radius, sort, centre]);

  useEffect(() => { run({ centre: DEFAULT }); /* initial */ }, []); // eslint-disable-line

  const onSearch = (e) => { e.preventDefault(); if (postcode.trim()) run({ postcode: postcode.trim() }); };

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:py-8">
      <Segmented />
      <form onSubmit={onSearch} className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input data-testid="discover-postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Search a postcode (e.g. BS1 4DJ)" className={`${inputCls} pl-9`} />
      </form>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <select data-testid="discover-sort" value={sort} onChange={(e) => { setSort(e.target.value); }} className={`${inputCls} !min-h-[44px] px-2 text-sm`}>
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select data-testid="discover-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value))} className={`${inputCls} !min-h-[44px] px-2 text-sm`}>
          {[1, 2, 5, 10].map((r) => <option key={r} value={r}>{r} km</option>)}
        </select>
      </div>

      {state === "loading" && <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}

      {state === "unavailable" && (
        <EmptyState testid="discover-fsa-unavailable" icon={AlertTriangle} title="FSA temporarily unavailable" description="The Food Standards Agency ratings service didn't respond. Please try again shortly — we never show fabricated results." />
      )}
      {state === "error" && <p data-testid="discover-error" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{errorMsg}</p>}

      {state === "ok" && (items.length === 0 ? (
        <EmptyState testid="discover-empty" icon={Compass} title="No prospects here" description="No FSA establishments in this area. Try a different postcode or a larger radius." />
      ) : (
        <div className="space-y-3" data-testid="discover-list">
          {items.map((p) => <ProspectCard key={p.source_establishment_id} p={p} onAdd={setSelected} />)}
          <button data-testid="discover-load-more" onClick={() => run({ page: page + 1 })} className="w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Load more</button>
        </div>
      ))}

      <AddToLeadsDialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)} prospect={selected} onDone={() => run({ centre })} />
    </div>
  );
}
